/**
 * Identifier helpers.
 *
 * Idempotency keys are supplied by the client and hashed before storage, so a
 * retry cannot create a second diagnosis (and therefore cannot consume a second
 * quota unit) while the database never stores arbitrary client text.
 */

import { sha256HexOfString } from './share-token';

const KEY_MIN_LENGTH = 16;
const KEY_MAX_LENGTH = 200;
const KEY_PATTERN = /^[A-Za-z0-9._~-]+$/;

/** Generate a client-side idempotency key. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Validate a client-supplied idempotency key. Rejects anything that is not a
 * bounded, opaque, URL-safe token.
 */
export function isValidIdempotencyKey(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.length < KEY_MIN_LENGTH || value.length > KEY_MAX_LENGTH) return false;
  return KEY_PATTERN.test(value);
}

/** The value actually stored in `diagnoses.idempotency_key`. */
export function hashIdempotencyKey(key: string): string {
  return sha256HexOfString(`api-rescue-desk:idempotency:${key}`);
}

export function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Shared by the worker and the browser so the same key is reused on retry. */
export const IDEMPOTENCY_HEADER = 'idempotency-key';
export const REQUEST_ID_HEADER = 'x-request-id';
